/**
 * Shared PDF utilities for Visits reports.
 * Centralized here so each report builder doesn't duplicate header/footer logic.
 */
import jsPDF from 'jspdf';

/**
 * Compact left-aligned navy header used by the email-PDF builders.
 * Returns the y-coordinate where table/content should start.
 */
export function drawCompactHeader(doc, { title, subtitle, startY = 22, color = [30, 58, 69] } = {}) {
  doc.setFontSize(16);
  doc.setTextColor(color[0], color[1], color[2]);
  doc.text(title || 'Report', 14, 15);
  if (subtitle) {
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(subtitle, 14, 22);
    doc.setTextColor(0);
    return startY + 3; // push content below subtitle
  }
  doc.setTextColor(0);
  return startY;
}

/**
 * Build a header block for a PDF report.
 * Returns the y-coordinate where content should start.
 */
export function drawReportHeader(doc, { title, subtitle, dateLabel } = {}) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text(title || 'Report', pageWidth / 2, 15, { align: 'center' });

  if (subtitle) {
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(subtitle, pageWidth / 2, 22, { align: 'center' });
  }

  if (dateLabel) {
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(dateLabel, pageWidth / 2, subtitle ? 28 : 22, { align: 'center' });
    doc.setTextColor(0);
  }

  return subtitle ? 32 : 26;
}

/**
 * Footer with timestamp + page numbers, applied to all pages.
 */
export function drawReportFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const ts = new Date().toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Generated ${ts}`, 10, pageHeight - 6);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 10, pageHeight - 6, { align: 'right' });
    doc.setTextColor(0);
  }
}

/**
 * Convert a jsPDF doc to a base64 string suitable for emailing as an attachment.
 */
export function pdfToBase64(doc) {
  // jsPDF's output('datauristring') returns "data:application/pdf;base64,XXX"
  const dataUri = doc.output('datauristring');
  const idx = dataUri.indexOf(',');
  return idx >= 0 ? dataUri.slice(idx + 1) : dataUri;
}

/**
 * Convenience: create a new landscape jsPDF.
 */
export function newLandscapePdf() {
  return new jsPDF('landscape');
}

/**
 * Convenience: create a new portrait jsPDF.
 */
export function newPortraitPdf() {
  return new jsPDF('portrait');
}
